import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Sparkles, Settings2, Paperclip, Loader2, Trash2 } from "lucide-react";
import { api, errMsg, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/StatusBadge";
import { DataTable } from "@/components/forms/DataTable";
import { EntryDialog } from "@/components/forms/EntryDialog";
import { ImportDialog } from "@/components/forms/ImportDialog";
import { ReasonDialog } from "@/components/forms/ReasonDialog";
import { DownloadMenu } from "@/components/forms/DownloadMenu";

export const FormDataView = ({ templateId, eyebrow = "Forms & Data" }) => {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState("");
  const [del, setDel] = useState(null);
  const [filters, setFilters] = useState({});
  const load = useCallback(() => api.get(`/forms/templates/${templateId}/rows`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [templateId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const t = d.template;
  const base = `/forms/templates/${templateId}`;
  const save = async (row, data, status) => {
    try { await api.put(`${base}/rows/${row.row_id}`, { data, status }); toast.success("Entry updated"); load(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  return (
    <div>
      <PageHeader eyebrow={eyebrow} title={t.name} testid="data-page-title">
        {d.can_edit && t.fields.length > 0 && <Button onClick={() => setDlg("entry")} data-testid="data-add-entry"><Plus className="w-4 h-4 mr-1" />Add entry</Button>}
        {d.can_edit && <Button variant="outline" onClick={() => setDlg("import")} data-testid="data-import-button"><Sparkles className="w-4 h-4 mr-1 text-amber-600" />Import records</Button>}
        <DownloadMenu label="Download form" path={`${base}/blank`} filename={`${t.name} - blank form`} disabled={!t.fields.length} testid="data-download-form" />
        <DownloadMenu label="Download data" path={`${base}/export`} filename={t.name} disabled={!d.rows.length} testid="data-download-data"
          params={Object.fromEntries(Object.entries(filters).filter(([, v]) => v))} />
        {d.can_edit && <Button variant="outline" asChild data-testid="data-edit-fields"><Link to={`/forms/${templateId}/edit`}><Settings2 className="w-4 h-4 mr-1" />Edit fields</Link></Button>}
        {d.can_delete && t.kind === "custom" && <Button variant="ghost" onClick={() => setDlg("delete-tpl")} data-testid="data-delete-template"><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
      </PageHeader>
      {t.description && <p className="text-sm text-slate-600 -mt-3 mb-4">{t.description}</p>}
      {t.source_files?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4 text-xs" data-testid="data-source-files">
          {t.source_files.map((s) => (
            <button key={s.upload_id} onClick={() => downloadBlob(`${base}/source/${s.upload_id}`, s.filename)} data-testid={`source-file-${s.upload_id}`}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600 hover:text-slate-900"><Paperclip className="w-3 h-3" />{s.filename}</button>
          ))}
        </div>
      )}
      {t.fields.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-10 text-center" data-testid="data-no-fields">
          <div className="font-semibold text-slate-900">No fields set up yet</div>
          <p className="text-sm text-slate-600 mt-1">Upload your current file with "Import records" — AI will read its fields and entries for you to review.</p>
        </div>
      ) : (
        <DataTable template={t} rows={d.rows} canEdit={d.can_edit} canDelete={d.can_delete} onSave={save} onDelete={setDel} onFilters={setFilters} />
      )}
      <EntryDialog open={dlg === "entry"} onClose={() => setDlg("")} template={t} onSaved={load} />
      <ImportDialog open={dlg === "import"} onClose={() => setDlg("")} template={t} onDone={load} />
      <ReasonDialog open={!!del} onClose={() => setDel(null)} danger title="Permanently delete this entry?" confirmLabel="Delete entry"
        onConfirm={async (reason) => { try { await api.delete(`${base}/rows/${del.row_id}`, { data: { reason } }); toast.success("Entry deleted"); load(); } catch (e) { toast.error(errMsg(e)); } }} />
      <ReasonDialog open={dlg === "delete-tpl"} onClose={() => setDlg("")} danger title="Delete this template and all its entries?"
        description="This cannot be undone. The deletion is recorded in the audit log." confirmLabel="Delete template"
        onConfirm={async (reason) => { try { await api.delete(base, { data: { reason } }); toast.success("Template deleted"); nav("/forms"); } catch (e) { toast.error(errMsg(e)); } }} />
    </div>
  );
};

export default function FormDataPage() {
  const { id } = useParams();
  return <FormDataView templateId={id} />;
}
